import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    FormHelperText,
    Typography,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    CircularProgress,
} from '@mui/material';
import {
    checkIfSerbianPhoneNumber,
    checkPhoneNumber,
    common,
    damageDescriptionCharacterLimit,
    formatPhoneValue,
    GenericObject,
    getApplicationId,
    getIsPortal,
    getIsSalesForce,
    ScreenProps,
} from '@fnol/library/src/lib';
import {
    TextField,
    ComboBox,
    StepWrapper,
    StepperControls,
    StyledCardSection,
    YesNoSelector,
    AddressComboBox,
} from '@fnol/library/src/components';
import {
    translations,
    insuranceGroup,
    questionMaxLength,
    question10MaxLength,
    question50MaxLength,
    personType,
    legalPersonType,
    objectType,
    smsLinkPersonType,
    damagedRoomsOptions,
    constValues,
    personValidationFields,
    causeType,
} from '../../lib/consts';
import { useApi } from '@fnol/library/src/hooks';
import { getObjectType, getDamageTypes, getPersonValidation, getStreetNames } from '../../rpc/actions';
import {
    showOwnerSelector,
    prepareDamageTypes,
    scrollToFirstError,
    preparePersonValidationData,
    prepareStreetNames,
} from '../../lib/helpers';
import { useValidateIndividual, useValidateLegalEntity } from '../../lib/customHooks';
import OwnerSelector from '../OwnerSelector';
import SmsLinkSelector from '../SmsLinkSelector';
import CauseType from '../CauseType';
import './damage.css';

interface FormData {
    objectType: string;
    objectTypeList: Array<GenericObject>;
    groupInsuranceMethod: number;
    addressObjectSameAsSubmitter: string;
    cities: Array<GenericObject>;
    objectCity: any;
    objectStreet: string;
    objectStreetNumber: string;
    objectCode: string;
    ownerIndividualName: string;
    ownerLegalEntityName: string;
    submitterType: legalPersonType;
    personRoles: Array<{
        id: number;
        naziv: string;
        prikazFL: boolean;
        prikazPL: boolean;
        unosVlasnika: boolean;
    }>;
    submitterIndividualPersonRole: number;
    submitterIndividualCountry: string;
    submitterLegalEntityPersonRole: number;
    submitterLegalEntityCountry: string;
    damagedObject: string;
    ownerType: legalPersonType;
    isShowSMSLink: boolean;
    objectHasSmsLink: string;
    objectSmsLinkType: smsLinkPersonType;
    objectSmsMobile: string;
    cause: number;
    damageTypes: [GenericObject];
    allDamageTypes: [GenericObject];
    streetNames: GenericObject[];
    damagedRooms: number;
    damageType: string[];
    damageDescription: string;
    submittedSuccessfully: boolean;
    validationError: string;
    ownerIndividualUCN: string;
    ownerIndividualEmail: string;
    ownerIndividualPhone: string;
    ownerLegalEntityID: string;
    ownerLegalEntityEmail: string;
}

const ClaimDetailScreen = ({ navigator }: ScreenProps) => {
    const [currentStep, { goForward, goBack }] = navigator;
    const { values, errors, touched, isSubmitting, setErrors, setTouched, setFieldValue, handleChange, submitForm } =
        useFormikContext<FormData>();
    const [isPortal, setIsPortal] = useState(true);
    const [isFirstPass, setIsFirstPass] = useState(true);
    const [showDamagedRooms, setShowDamagedRooms] = useState(false);
    const [isShowOwnerSelector, setIsShowOwnerSelector] = useState(false);
    const [isSubmitterCountrySrb, setisSubmitterCountrySrb] = useState(true);
    const ownerIndividualValidation = useValidateIndividual(personType.owner);
    const ownerLegalEntityValidation = useValidateLegalEntity(personType.owner);
    const dataForValidation: GenericObject = {};

    const saveObjectType = (res: GenericObject) => {
        setFieldValue('objectTypeList', res.data);
    };

    const saveDamageTypes = (res: GenericObject) => {
        setFieldValue('allDamageTypes', prepareDamageTypes(res.data));
    };
    const savePersonValidation = (res: GenericObject) => {
        setTouched(
            {
                ownerIndividualPhone: true,
                ownerIndividualEmail: true,
                ownerIndividualUCN: true,
                ownerLegalEntityEmail: true,
            },
            false,
        );

        const existingErrors: GenericObject = {};

        if (values.ownerType === legalPersonType.individual) {
            preparePersonValidationData(res.data, personValidationFields.phone)[0] &&
            formatPhoneValue(values.ownerIndividualPhone) !== ''
                ? (existingErrors.ownerIndividualPhone = preparePersonValidationData(
                      res.data,
                      personValidationFields.phone,
                  )[0])
                : null;
            preparePersonValidationData(res.data, personValidationFields.email)[0]
                ? (existingErrors.ownerIndividualEmail = preparePersonValidationData(
                      res.data,
                      personValidationFields.email,
                  )[0])
                : null;
            preparePersonValidationData(res.data, personValidationFields.ucn)[0]
                ? (existingErrors.ownerIndividualUCN = preparePersonValidationData(
                      res.data,
                      personValidationFields.ucn,
                  )[0])
                : null;
        }

        if (values.ownerType === legalPersonType.legal) {
            preparePersonValidationData(res.data, personValidationFields.email)[0]
                ? (existingErrors.ownerLegalEntityEmail = preparePersonValidationData(
                      res.data,
                      personValidationFields.email,
                  )[0])
                : null;
        }

        if (Object.keys(existingErrors).length === 0) {
            if (!values.damagedRooms || values.damagedRooms === 4 || !showSpecification()) {
                submitForm();
            } else {
                goForward();
            }
        } else {
            setTimeout(() => {
                setErrors(existingErrors);
            });
        }
    };

    const { response: damageTypes, fetchMethod: fetchDamageTypes } = useApi(() => getDamageTypes(), saveDamageTypes);
    const { response: personValidation, fetchMethod: fetchPersonValidation } = useApi(
        () => getPersonValidation(dataForValidation),
        savePersonValidation,
    );

    let cityCodeForStreetSearch = '';
    if (values.objectCity && values.objectCity !== '' && !!values.objectCity.code)
        cityCodeForStreetSearch = values.objectCity.code;
    const saveStreetNames = (res: GenericObject) => {
        const streetNames = prepareStreetNames(res.data);
        setFieldValue('streetNames', streetNames);
    };
    const { response: streetNames, fetchMethod: fetchStreetNames } = useApi(
        () => getStreetNames(cityCodeForStreetSearch),
        saveStreetNames,
    );

    const showDamagedRoomsCheck = (damageTypesArray: GenericObject[]) => {
        let checkedDamageTypeToShow = false;
        const damageTypesToShow = damageTypesArray.filter((damageType) => damageType.shouldShowDamagedRooms === true);

        damageTypesArray.map((damageType: GenericObject) => {
            if (damageType.checked) {
                if (!damageTypesToShow.includes(damageType)) {
                    checkedDamageTypeToShow = false;
                } else {
                    checkedDamageTypeToShow = true;
                }
            }
        });
        if (checkedDamageTypeToShow) {
            return true;
        } else {
            return false;
        }
    };

    const showSpecification = () => {
        if (values.damageTypes.length > 0) {
            const damageTypesRequerdForShowSpecification = values.damageTypes.filter(
                (damageType) => damageType.shouldShowSpcification === true && damageType.checked,
            );

            if (damageTypesRequerdForShowSpecification.length > 0) return true;
        }

        return false;
    };

    const toggleDamageType = (id: number): void => {
        const newDamageTypes: GenericObject[] = [];
        values.damageTypes.map((damageType: GenericObject) => {
            if (damageType.id === id) {
                const toggledDamageType = damageType;
                toggledDamageType.checked = !damageType.checked;
                newDamageTypes.push(toggledDamageType);
            } else {
                newDamageTypes.push(damageType);
            }
        });
        setFieldValue('damageTypes', newDamageTypes);
        setFieldValue('damagedRooms', 0);
    };

    const { response: objectTypeList, fetchMethod: fetchObjectType } = useApi(() => getObjectType(), saveObjectType);

    useEffect(() => {
        let personRole: number;

        if (values.damageTypes.length <= 0) fetchDamageTypes();
        const appId = sessionStorage.getItem(`house_appID`);
        if (appId && getIsPortal(parseInt(appId))) {
            setIsPortal(true);
        } else {
            setIsPortal(false);
        }
        fetchObjectType();

        if (values.submitterType === legalPersonType.individual) {
            personRole = values.submitterIndividualPersonRole;
            setisSubmitterCountrySrb(values.submitterIndividualCountry === constValues.countrySerbiaId);
            if (values.submitterIndividualCountry !== constValues.countrySerbiaId) {
                setFieldValue('addressObjectSameAsSubmitter', 'false');
            }
        } else {
            personRole = values.submitterLegalEntityPersonRole;
            setisSubmitterCountrySrb(values.submitterLegalEntityCountry === constValues.countrySerbiaId);
            if (values.submitterLegalEntityCountry !== constValues.countrySerbiaId) {
                setFieldValue('addressObjectSameAsSubmitter', 'false');
            }
        }

        // for standard policy owner is fetched form policy data
        if (values.groupInsuranceMethod === insuranceGroup.policyNumber) setIsShowOwnerSelector(false);
        // for other policies and damage on insured object owner is shown based on selected role for submitter
        else if (values.damagedObject === objectType.insured)
            setIsShowOwnerSelector(showOwnerSelector(values.personRoles, personRole)[0]);
        // in other cases owner is shown
        else setIsShowOwnerSelector(true);
    }, []);

    useEffect(() => {
        setShowDamagedRooms(showDamagedRoomsCheck(values.damageTypes));
    }, [values.damageTypes]);

    useEffect(() => {
        const checkedDamageTypes = values.damageTypes.filter((damageType) => damageType.checked);
        const showSMSSelector =
            getIsSalesForce(getApplicationId()) ||
            !!!(
                (values.cause === causeType.installationDamage ||
                    values.cause === causeType.seepageDamage ||
                    values.cause === causeType.storm) &&
                checkedDamageTypes.length === 1 &&
                checkedDamageTypes[0].id === constValues.damageTypeWallsCeiling
            );
        setFieldValue('isShowSMSLink', showSMSSelector);
    }, [values.damageTypes, values.cause]);

    const handleNext = (): void => {
        setFieldValue('validationError', '');
        setTouched(
            {
                objectType: true,
                addressObjectSameAsSubmitter: true,
                objectCity: true,
                objectStreet: true,
                objectStreetNumber: true,
                objectCode: true,
                ownerType: true,
                ...ownerIndividualValidation.touched,
                ...ownerLegalEntityValidation.touched,
                objectHasSmsLink: true,
                objectSmsLinkType: true,
                objectSmsMobile: true,
                cause: true,
                damagedRooms: true,
                damageType: true,
                damageDescription: true,
            },
            false,
        );

        let existingErrors: GenericObject = {};
        let valid = true;

        if (!values.objectType) {
            existingErrors.objectType = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (
            !isPortal &&
            (values.groupInsuranceMethod !== insuranceGroup.policyNumber ||
                (values.groupInsuranceMethod === insuranceGroup.policyNumber &&
                    values.damagedObject === objectType.other))
        ) {
            if (!values.addressObjectSameAsSubmitter) {
                existingErrors.addressObjectSameAsSubmitter = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            } else if (values.addressObjectSameAsSubmitter === 'false') {
                if (!values.objectCity) {
                    existingErrors.objectCity = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (!values.objectStreet) {
                    existingErrors.objectStreet = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (!values.objectStreetNumber) {
                    existingErrors.objectStreetNumber = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
            }
        }

        if (!isSubmitterCountrySrb) {
            if (!values.objectCity) {
                existingErrors.objectCity = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (!values.objectStreet) {
                existingErrors.objectStreet = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
        }

        if (isShowOwnerSelector) {
            if (!values.ownerType || values.ownerType === legalPersonType.initialValue) {
                existingErrors.ownerType = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
        }

        if (values.ownerType === legalPersonType.individual) {
            existingErrors = {
                ...existingErrors,
                ...ownerIndividualValidation.errors,
            };
            if (!ownerIndividualValidation.isValid) valid = false;
        }

        if (values.ownerType === legalPersonType.legal) {
            existingErrors = {
                ...existingErrors,
                ...ownerLegalEntityValidation.errors,
            };
            if (!ownerLegalEntityValidation.isValid) valid = false;
        }

        if (!values.cause) {
            existingErrors.cause = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (values.isShowSMSLink) {
            if (!values.objectHasSmsLink) {
                existingErrors.objectHasSmsLink = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (values.objectHasSmsLink === 'true' && !values.objectSmsLinkType) {
                existingErrors.objectSmsLinkType = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (
                values.objectHasSmsLink === 'true' &&
                !values.objectSmsMobile &&
                values.objectSmsLinkType === smsLinkPersonType.thirdParty
            ) {
                existingErrors.objectSmsMobile = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (
                values.objectSmsMobile &&
                values.objectHasSmsLink === 'true' &&
                values.objectSmsLinkType === smsLinkPersonType.thirdParty &&
                (!checkPhoneNumber(values.objectSmsMobile) || !checkIfSerbianPhoneNumber(values.objectSmsMobile))
            ) {
                existingErrors.objectSmsMobile = translations.validationMessages.PHONE_NUMBER_FORMAT_SERBIAN;
                valid = false;
            }
        }

        const checkedDamageTypes = values.damageTypes.filter((damageType) => damageType.checked);
        if (checkedDamageTypes.length <= 0) {
            existingErrors.damageType = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (showDamagedRooms && (!values.damagedRooms || values.damagedRooms === 0)) {
            existingErrors.damagedRooms = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (values.damageDescription === '') {
            existingErrors.damageDescription = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        setTimeout(() => {
            setErrors(existingErrors);
        });
        scrollToFirstError(existingErrors);

        if (valid) {
            if (values.ownerType === legalPersonType.individual) {
                dataForValidation[personValidationFields.ucn] = values.ownerIndividualUCN;
                dataForValidation[personValidationFields.email] = values.ownerIndividualEmail;
                dataForValidation[personValidationFields.phone] = formatPhoneValue(values.ownerIndividualPhone);
                fetchPersonValidation();
            } else if (values.ownerType === legalPersonType.legal) {
                dataForValidation[personValidationFields.email] = values.ownerLegalEntityEmail;
                fetchPersonValidation();
            } else if (!values.damagedRooms || values.damagedRooms === 4 || !showSpecification()) {
                submitForm();
            } else {
                goForward();
            }
        }
    };

    useEffect(() => {
        if (isFirstPass) {
            setIsFirstPass(false);
        } else if (values.objectCity && values.objectCity !== '' && !!values.objectCity.code) {
            setFieldValue('objectStreet', '');
            fetchStreetNames();
        }
    }, [values.objectCity]);

    useEffect(() => {
        if (!values.damagedRooms || values.damagedRooms === 4 || !showSpecification()) {
            if (values.submittedSuccessfully) {
                goForward();
            }
        }
    }, [values.submittedSuccessfully]);

    const handleGoBack = (): void => {
        goBack();
    };

    const handleChangeObjectType = (e: { target: { value: any } }): any => {
        setFieldValue('objectType', e.target.value);

        values.damageTypes.map((damageType: GenericObject) => {
            if (damageType.checked) {
                return (damageType.checked = !damageType.checked);
            }
        });
    };

    const handleChangeAddressObjectSameAsSubmitter = () => {
        setFieldValue('objectCity', '');
        setFieldValue('objectStreet', '');
        setFieldValue('objectStreetNumber', '');
    };

    useEffect(() => {
        if (!values.damagedRooms || values.damagedRooms === 4 || !showSpecification()) {
            setFieldValue('skipSteps', [3]);
        } else {
            setFieldValue('skipSteps', []);
        }
    }, [values.damagedRooms]);

    useEffect(() => {
        if (values.objectType && values.objectType !== '') {
            const filteredTypes = values.allDamageTypes.filter(
                (damageType) => values.objectType === damageType.objectTypeId,
            );

            setFieldValue('damageTypes', filteredTypes);
        }
    }, [values.objectType]);

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px', paddingTop: '15px' }} variant="caption">
                    {translations.claimDetail.OBJECT_DETAILS}
                </Typography>
                <FormHelperText error={false}>
                    <>{translations.claimDetail.OBJECT_TYPE_1}</>
                </FormHelperText>
                <FormControl error={!!(touched.objectType && errors.objectType)} required>
                    <InputLabel>{translations.claimDetail.OBJECT_TYPE}</InputLabel>
                    <Select
                        id="objectType"
                        name="objectType"
                        value={values.objectType || ''}
                        label={translations.claimDetail.OBJECT_TYPE}
                        onChange={handleChangeObjectType}
                    >
                        {values.objectTypeList?.map((option: GenericObject) => {
                            return (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.naziv}
                                </MenuItem>
                            );
                        })}
                    </Select>
                    {touched.objectType && errors.objectType ? (
                        <FormHelperText error={true}>
                            <>{errors.objectType}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
                {!isPortal &&
                    (values.groupInsuranceMethod !== insuranceGroup.policyNumber ||
                        (values.groupInsuranceMethod === insuranceGroup.policyNumber &&
                            values.damagedObject === objectType.other)) &&
                    isSubmitterCountrySrb && (
                        <>
                            <Typography variant="subtitle1">
                                {translations.claimDetail.ADDRESS_OBJECT_IS_SAME_AS_SUBMITTER}
                            </Typography>
                            <FormControl>
                                <YesNoSelector
                                    name="addressObjectSameAsSubmitter"
                                    id="addressObjectSameAsSubmitter"
                                    idTrue="addressObjectSameAsSubmitterTrue"
                                    idFalse="addressObjectSameAsSubmitterFalse"
                                    onChange={handleChangeAddressObjectSameAsSubmitter}
                                />
                            </FormControl>
                        </>
                    )}
                {values.addressObjectSameAsSubmitter === 'false' && (
                    <>
                        <FormControl>
                            <Field
                                name="objectCity"
                                id="objectCity"
                                label={translations.CITY}
                                options={
                                    values.cities || [
                                        {
                                            id: '',
                                            value: '',
                                            text: '',
                                            code: '',
                                            zipCode: '',
                                        },
                                    ]
                                }
                                onChange={handleChange}
                                defaultValue={values.objectCity}
                                as={ComboBox}
                                inputProps={{
                                    maxLength: question50MaxLength,
                                }}
                                error={!!(touched.objectCity && errors.objectCity)}
                                required
                            />
                            {touched.objectCity && errors.objectCity ? (
                                <FormHelperText error={true}>
                                    <>{errors.objectCity}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>

                        <FormControl>
                            <Field
                                name="objectStreet"
                                id="objectStreet"
                                label={translations.ADDRESS}
                                options={
                                    values.streetNames || [
                                        {
                                            id: '',
                                            value: '',
                                            label: '',
                                        },
                                    ]
                                }
                                defaultValue={values.objectStreet}
                                as={AddressComboBox}
                                inputProps={{
                                    maxLength: question50MaxLength,
                                }}
                                error={!!(touched.objectStreet && errors.objectStreet)}
                                required
                            />
                            {touched.objectStreet && errors.objectStreet ? (
                                <FormHelperText error={true}>
                                    <>{errors.objectStreet}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>

                        <FormControl>
                            <Field
                                name="objectStreetNumber"
                                id="objectStreetNumber"
                                label={translations.ADDRESS_NO}
                                as={TextField}
                                inputProps={{
                                    maxLength: question10MaxLength,
                                }}
                                error={!!(touched.objectStreetNumber && errors.objectStreetNumber)}
                                required
                            />
                            {touched.objectStreetNumber && errors.objectStreetNumber ? (
                                <FormHelperText error={true}>
                                    <>{errors.objectStreetNumber}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}
                {values.groupInsuranceMethod === insuranceGroup.publicUtility && !isPortal && (
                    <FormControl>
                        <Field
                            name="objectCode"
                            id="objectCode"
                            label={translations.claimDetail.OBJECT_CODE}
                            as={TextField}
                            inputProps={{
                                maxLength: question50MaxLength,
                            }}
                            error={!!(touched.objectCode && errors.objectCode)}
                        />
                        {touched.objectCode && errors.objectCode ? (
                            <FormHelperText error={true}>
                                <>{errors.objectCode}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                )}
                {isShowOwnerSelector && <OwnerSelector></OwnerSelector>}
                <Typography style={{ fontWeight: 'bold', fontSize: '18px', paddingTop: '15px' }} variant="caption">
                    {translations.claimDetail.CLAIM_DETAILS}
                </Typography>
                <CauseType></CauseType>
                {values.objectType && values.objectType !== '' && values.damageTypes.length > 0 && (
                    <FormControl error={!!(touched.damageType && errors.damageType)} required>
                        <Typography variant="subtitle1">{translations.DAMAGE_TYPE_LABEL}</Typography>
                        <List dense id="damageType">
                            {values.damageTypes.map((damageType: GenericObject) => {
                                return (
                                    <ListItem key={damageType.id}>
                                        <Checkbox
                                            onChange={() => toggleDamageType(damageType.id)}
                                            checked={damageType.checked}
                                        />
                                        <ListItemText primary={damageType.primary} secondary={damageType.secondary} />
                                    </ListItem>
                                );
                            })}
                        </List>
                        {touched.damageType && errors.damageType ? (
                            <FormHelperText error={true}>
                                <>{errors.damageType}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                )}
                {showDamagedRooms && (
                    <>
                        <FormControl error={!!(touched.damagedRooms && errors.damagedRooms)} required>
                            <InputLabel>{translations.DAMAGED_ROOMS_LABEL}</InputLabel>
                            <Field
                                id="damagedRooms"
                                name="damagedRooms"
                                label={translations.DAMAGED_ROOMS_LABEL}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                as={Select}
                            >
                                {damagedRoomsOptions.map((option) => {
                                    return (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.value}
                                        </MenuItem>
                                    );
                                })}
                            </Field>
                            {touched.damagedRooms && errors.damagedRooms ? (
                                <FormHelperText error={true}>
                                    <>{errors.damagedRooms}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}
                <>
                    <FormControl error={!!(touched.damageDescription && errors.damageDescription)} required>
                        <Field
                            id="damageDescription"
                            name="damageDescription"
                            multiline={true}
                            maxRows={3}
                            minRows={3}
                            label={translations.DAMAGE_DESCRIPTION}
                            as={TextField}
                            inputProps={{
                                maxLength: damageDescriptionCharacterLimit,
                            }}
                            helperText={`${translations.REMAINING_CHARACTERS} ${
                                damageDescriptionCharacterLimit - values.damageDescription.length
                            }`}
                        />
                        {touched.damageDescription && errors.damageDescription ? (
                            <FormHelperText error={true}>
                                <>{errors.damageDescription}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                </>

                {values.isShowSMSLink && <SmsLinkSelector></SmsLinkSelector>}
            </StyledCardSection>

            {isSubmitting ? (
                <CircularProgress />
            ) : (
                <StepperControls
                    onNext={handleNext}
                    onNextLabel={common.NEXT}
                    onPrevious={handleGoBack}
                    onPreviousLabel={common.PREVIOUS}
                    idNext="claimDetailScreenNext"
                    idPrevious="claimDetailScreenPrevious"
                />
            )}
        </StepWrapper>
    );
};

export default ClaimDetailScreen;
